import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import FloatingResourceAddMenu from 'components/FloatingResourceAddMenu';
import Grid from 'components/Grid';
import AddButton from 'components/buttons/AddButton';

import { useFloatingResourceAddContext } from 'hooks/useFloatingResourceAddContext';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ResourceAddButton: FunctionComponent<Props> = ({
    className = '',
    defaultAssetType,
    defaultResource,
    onOpenChange,
    testId = TEST_ID
}) => {
    const { clearOpenRequest, openRequest } = useFloatingResourceAddContext();
    const [isOpen, setIsOpen] = useState(false);
    const [menuKey, setMenuKey] = useState(0);
    const { pathname } = useLocation();
    const previousPathnameRef = useRef(pathname);

    const handleOpen = () => {
        setIsOpen(true);
        onOpenChange?.(true);
    };

    const handleClose = () => {
        setIsOpen(false);
        onOpenChange?.(false);
        clearOpenRequest();
    };

    useEffect(() => {
        if (openRequest) {
            setMenuKey(previous => previous + 1);
            setIsOpen(true);
            onOpenChange?.(true);
        }
    }, [onOpenChange, openRequest]);

    // Clear a pending open request on unmount (e.g. navigating to a page that hides this
    // button) so returning later does not auto-reopen a stale, wrongly-prefilled form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => clearOpenRequest, []);

    // Collapse the menu on route change so each page starts on its own contextual default
    // rather than a stale form left open from the previous route.
    useEffect(() => {
        if (previousPathnameRef.current === pathname) {
            return;
        }

        previousPathnameRef.current = pathname;
        setIsOpen(false);
        onOpenChange?.(false);
        clearOpenRequest();
    }, [clearOpenRequest, onOpenChange, pathname]);

    return (
        <Grid className={`${className} ${styles.resourceAddButton}`} testId={testId}>
            {isOpen ? (
                <FloatingResourceAddMenu
                    defaultAssetType={defaultAssetType}
                    defaultResource={defaultResource}
                    key={menuKey}
                    onClose={handleClose}
                    openRequest={openRequest}
                />
            ) : (
                <AddButton onClick={handleOpen} />
            )}
        </Grid>
    );
};

export default ResourceAddButton;
